import { CSSProperties, FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

import config from "@Config/index";
import { AppState } from "@Store/index";
import { getPickerLanguages } from "@Utils/languageCore";
import { getWhitelabelDefaultCurrency } from "@Utils/whitelabelLocalization";
import wrapperHOC from "@Utils/wrapperHOC";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import currencyData, { ALLOWED_CURRENCY_CODES } from "constants/currencyConstants";
import getCookie from "helpers/getCookie";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import setCookie from "helpers/setCookie";
import useIsApp from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import useLanguageSelector from "hooks/useLanguageSelector";
import useTranslationHook from "hooks/useTranslationHook";
import { CHINA_HEADER_LANGUAGE_CODES } from "locales/langConfig/langConfig";

import flagCN from "../../assets/flags/cn.svg";
import flagHK from "../../assets/flags/hk.svg";
import flagTW from "../../assets/flags/tw.svg";
import flagFR from "../../assets/flags/fr.svg";
import flagES from "../../assets/flags/es.svg";
import flagDE from "../../assets/flags/de.svg";
import flagVN from "../../assets/flags/vn.svg";
import flagTH from "../../assets/flags/th.svg";
import flagTR from "../../assets/flags/tr.svg";
import earth from "../../assets/flags/earth.png";
import CURRENCY_FLAG from "./currencyFlags";
import useCopy from "../../useCopy";
import styles from "./PreferencesModal.module.scss";

/**
 * HomeV2's language and currency picker, in Airbnb's "Language and region / Currency"
 * style, cut down to a small modal: two underline tabs over a grid of tiles, each a
 * name over a quiet second line, the current choice outlined. A tile APPLIES ON CLICK —
 * no Reset/Apply step.
 *
 * The behaviour is the shared LanguageSelectorModal's, kept exactly: the same language
 * list (getPickerLanguages: the Chinese languages included, the SHOW_NEW_LOCALES gate),
 * the same `handleLanguageChange` navigation (a Chinese language on .com goes to .cn),
 * the same `user_currency` cookie and reload, the same analytics events. Only the
 * presentation is new, and the Chinese languages are grouped (see below).
 */

interface Props {
  onClose: () => void;
}

/** A region line under each language, as Airbnb shows one under each locale. */
const LANGUAGE_REGION: Record<string, string> = {
  // The Chinese languages: which script, and where — short, so the line fits beside a flag.
  zh: "Simplified Chinese",
  "zh-tw": "Traditional · Taiwan",
  "zh-hk": "Traditional · Hong Kong",
  en: "Global",
  fr: "France",
  es: "España",
  de: "Deutschland",
  vi: "Việt Nam",
  th: "ประเทศไทย",
  tr: "Türkiye",
};

/**
 * The region lines that translate, by their copy key. Not the Chinese languages': their
 * lines stay English ("Simplified Chinese", "Traditional · Taiwan"…), as on the live .cn
 * picker — under a name already written in Chinese, a Chinese line would only repeat it.
 */
const LANGUAGE_REGION_KEY: Record<string, string> = {
  en: "home.v3.prefs.region.global",
};

/** Full currency names; the shared data only carries short ones ("Pound (£)"). */
const CURRENCY_NAME: Record<string, string> = {
  USD: "US dollar",
  GBP: "Pound sterling",
  EUR: "Euro",
  CNY: "Chinese yuan",
  CAD: "Canadian dollar",
  INR: "Indian rupee",
  AUD: "Australian dollar",
  AED: "UAE dirham",
  SGD: "Singapore dollar",
  NZD: "New Zealand dollar",
  KRW: "South Korean won",
  DKK: "Danish krone",
  HKD: "Hong Kong dollar",
  TWD: "New Taiwan dollar",
  // MENA display currencies (P1D-5557).
  SAR: "Saudi riyal",
  KWD: "Kuwaiti dinar",
  QAR: "Qatari riyal",
  BHD: "Bahraini dinar",
  OMR: "Omani rial",
  JOD: "Jordanian dinar",
  ILS: "Israeli new shekel",
  TRY: "Turkish lira",
  EGP: "Egyptian pound",
  MAD: "Moroccan dirham",
  DZD: "Algerian dinar",
  TND: "Tunisian dinar",
};

/** Each language's country flag, as its region line names it; English is global, so the
 * earth (the proof strip's, cropped to the sphere). Spain's is the plain civil flag: the
 * arms are lost at 24px anyway. */
const LANGUAGE_FLAG: Record<string, string> = {
  // Shown on .com only; on .cn the Chinese languages carry no flag (see below).
  zh: flagCN,
  "zh-tw": flagTW,
  "zh-hk": flagHK,
  en: earth,
  fr: flagFR,
  es: flagES,
  de: flagDE,
  vi: flagVN,
  th: flagTH,
  tr: flagTR,
};

const Flag: FC<{ src?: string }> = ({ src }) =>
  src ? (
    <span className={styles.flag} style={{ backgroundImage: `url(${src})` }} aria-hidden="true" />
  ) : (
    <span className={styles.flag} aria-hidden="true" />
  );

/** The selected tile's tick, in the brand pink — the one mark of colour on the tile. */
const Check: FC = () => (
  <svg className={styles.check} width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const PreferencesModal: FC<Props> = ({ onClose }) => {
  const { t } = useTranslationHook();
  const copy = useCopy();
  const userLanguage = useSelector(({ appData }: AppState) => appData.userLanguage) || "en";
  const track = ({ action, ...rest }: any) =>
    gtagEventEmitter({ action, category: "LANGUAGE_MODAL", ...rest });
  const { handleLanguageChange } = useLanguageSelector({ handleEventEmitter: track });
  const [tab, setTab] = useState<"language" | "currency">("language");
  // The visitor's chosen currency, or "" when none is set — then "Default" is selected.
  const currentCurrency = (
    (typeof document !== "undefined" && getCookie("user_currency")) ||
    ""
  ).toUpperCase();
  // Label for the "Default" tile only — not the visitor's chosen currency. Exchange-rate
  // baseCurrency tracks the cookie (see updateCurrencyExchangeRates), so using it here made
  // Default's subtitle mirror whatever was selected. Whitelabel partners may pin a default;
  // on amberstudent.com we show GBP as the site default when the cookie is cleared.
  const whitelabelDefaultCurrency = useSelector(({ whitelabelData }: AppState) =>
    getWhitelabelDefaultCurrency(whitelabelData?.config),
  );
  const defaultCode = (whitelabelDefaultCurrency || "GBP").toUpperCase();
  const defaultSymbol = currencyData.find((c) => c.standard_name === defaultCode)?.symbol;

  // The live picker's list (getPickerLanguages), on both sites: every language, the three
  // Chinese ones included (on .com they link out to .cn), vi/th/tr gated by
  // SHOW_NEW_LOCALES except the user's current one.
  const showNewLocales = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_NEW_LOCALES);
  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();
  const languages = useMemo(
    () =>
      getPickerLanguages({
        currentLanguage: userLanguage,
        showNewLocales,
        isMiniProgram,
        isApp,
      }).map((lang) => ({ code: lang.code, nativeLabel: lang.label })),
    [showNewLocales, userLanguage, isMiniProgram, isApp],
  );

  // The three Chinese languages as their own group, behind a rule: on .cn they lead the
  // list WITHOUT flags (a China flag beside the Taiwan and Hong Kong locales is a legal
  // risk there); on .com they close it, each with its own flag (China, Taiwan, Hong Kong).
  const chineseCodes: readonly string[] = CHINA_HEADER_LANGUAGE_CODES;
  const chineseLanguages = languages.filter((lang) => chineseCodes.includes(lang.code));
  const otherLanguages = languages.filter((lang) => !chineseLanguages.includes(lang));

  // DEMO: the Currency tab is always offered here, so the segmented control has its
  // two sides even where the currency flag is off (it is, locally). The list is the
  // same allow-list the live picker shows when the flag is on.
  const currencies = useMemo(
    () => currencyData.filter((c) => ALLOWED_CURRENCY_CODES.includes(c.standard_name)),
    [],
  );

  const pickLanguage = (code: string) => {
    onClose();
    if (code === userLanguage) return;
    track({ action: "APPLY_LANGUAGE", language: code });
    if (config.IS_CHINA_APP && code !== "zh") {
      const url = new URL(window.location.href);
      url.searchParams.set("amber_user_lang", code);
      window.history.replaceState(null, "", url.toString());
    }
    setTimeout(() => handleLanguageChange(code), 100);
  };

  const pickCurrency = (code: string) => {
    onClose();
    if (code === currentCurrency) return;
    track({ action: "APPLY_CURRENCY", currency: code || "default" });
    const baseDomain = window.location.hostname.split(".").slice(-2).join(".");
    setCookie({
      cname: "user_currency",
      cvalue: code,
      exdays: code ? 365 : -1,
      secure: true,
      domain: `.${baseDomain}`,
    });
    setTimeout(() => window.location.reload(), 100);
  };

  // The rail's position and width, read from the chosen tab (the two labels differ in
  // width), and re-read whenever the tab changes.
  const tabsRef = useRef<HTMLDivElement>(null);
  const [railStyle, setRailStyle] = useState<CSSProperties>({});
  useLayoutEffect(() => {
    const el = tabsRef.current?.querySelector<HTMLElement>(`[data-tab="${tab}"]`);
    if (!el) return;
    setRailStyle({
      "--tab-x": `${el.offsetLeft}px`,
      "--tab-w": `${el.offsetWidth}px`,
    } as CSSProperties);
  }, [tab]);

  const switchTab = (next: "language" | "currency") => {
    setTab(next);
    track({ action: `clicked_${next}_tab` });
  };

  // No flags anywhere on China (.cn): only words, as the Chinese languages already are there.
  const showFlags = !config.IS_CHINA_APP;

  const languageTile = (lang: { code: string; nativeLabel: string }, withFlag: boolean) => (
    <li key={lang.code}>
      <button
        type="button"
        className={`${styles.tile} ${lang.code === userLanguage ? styles.tileActive : ""}`}
        onClick={() => pickLanguage(lang.code)}
      >
        {withFlag && <Flag src={LANGUAGE_FLAG[lang.code]} />}
        <span className={styles.tileText}>
          <span className={styles.tileName}>{lang.nativeLabel}</span>
          <span className={styles.tileSub}>
            {LANGUAGE_REGION_KEY[lang.code]
              ? copy(LANGUAGE_REGION_KEY[lang.code], LANGUAGE_REGION[lang.code])
              : LANGUAGE_REGION[lang.code] || ""}
          </span>
        </span>
        {lang.code === userLanguage && <Check />}
      </button>
    </li>
  );

  return (
    <div className={styles.modal}>
      {/* Underline tabs; the pink rail slides to the chosen one, measured from it. .cn
          picks the language only, as the live China picker does, so a tab bar there
          would hold one tab: it has none, and the heading leads instead. */}
      {!config.IS_CHINA_APP && (
        <div className={styles.tabs} role="tablist" ref={tabsRef} style={railStyle}>
          <span className={styles.thumb} aria-hidden="true" />
          <button
            type="button"
            role="tab"
            aria-selected={tab === "language"}
            data-tab="language"
            className={`${styles.tab} ${tab === "language" ? styles.tabActive : ""}`}
            onClick={() => switchTab("language")}
          >
            {t("Language")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "currency"}
            data-tab="currency"
            className={`${styles.tab} ${tab === "currency" ? styles.tabActive : ""}`}
            onClick={() => switchTab("currency")}
          >
            {t("Currency")}
          </button>
        </div>
      )}

      <div
        className={`${styles.body} ${config.IS_CHINA_APP ? styles.bodyLanguageOnly : ""}`}
        role={config.IS_CHINA_APP ? undefined : "tabpanel"}
      >
        <h3 className={styles.heading}>
          {tab === "language" ? t("Choose a language") : t("Choose a currency")}
        </h3>
        <ul className={`${styles.grid} ${styles.gridFlags}`}>
          {tab === "language" && (
            <>
              {config.IS_CHINA_APP && chineseLanguages.map((lang) => languageTile(lang, false))}
              {config.IS_CHINA_APP && chineseLanguages.length > 0 && otherLanguages.length > 0 && (
                <li className={styles.groupRule} role="separator" aria-hidden="true" />
              )}
              {otherLanguages.map((lang) => languageTile(lang, showFlags))}
              {!config.IS_CHINA_APP && chineseLanguages.length > 0 && otherLanguages.length > 0 && (
                <li className={styles.groupRule} role="separator" aria-hidden="true" />
              )}
              {!config.IS_CHINA_APP && chineseLanguages.map((lang) => languageTile(lang, true))}
            </>
          )}
          {tab === "currency" && (
            <>
              <li>
                <button
                  type="button"
                  className={`${styles.tile} ${!currentCurrency ? styles.tileActive : ""}`}
                  onClick={() => pickCurrency("")}
                >
                  {showFlags && <Flag src={CURRENCY_FLAG[defaultCode]} />}
                  <span className={styles.tileText}>
                    {/* "Default", as the live site's currency picker words it. */}
                    <span className={styles.tileName}>
                      {copy("home.v3.prefs.currency.default", "Default")}
                    </span>
                    <span className={styles.tileSub}>
                      {!defaultSymbol || defaultSymbol === defaultCode
                        ? defaultCode
                        : `${defaultCode} – ${defaultSymbol}`}
                    </span>
                  </span>
                  {!currentCurrency && <Check />}
                </button>
              </li>
              {currencies.map((c) => (
                <li key={c.standard_name}>
                  <button
                    type="button"
                    className={`${styles.tile} ${
                      c.standard_name === currentCurrency ? styles.tileActive : ""
                    }`}
                    onClick={() => pickCurrency(c.standard_name)}
                  >
                    {showFlags && <Flag src={CURRENCY_FLAG[c.standard_name]} />}
                    <span className={styles.tileText}>
                      <span className={styles.tileName}>
                        {CURRENCY_NAME[c.standard_name]
                          ? copy(
                              `home.v3.prefs.currency.${c.standard_name.toLowerCase()}`,
                              CURRENCY_NAME[c.standard_name],
                            )
                          : c.full_name?.split("(")[0]}
                      </span>
                      <span className={styles.tileSub}>
                        {c.symbol === c.standard_name
                          ? c.standard_name
                          : `${c.standard_name} – ${c.symbol}`}
                      </span>
                    </span>
                    {c.standard_name === currentCurrency && <Check />}
                  </button>
                </li>
              ))}
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default wrapperHOC(PreferencesModal, {
  componentName: "PreferencesModal-HomeV2",
  showForChina: true,
  showForWhiteLable: false,
});
